// TrackingPage — vista única de operación en vivo (M13).
//
// Reemplaza /operacion + /tracking en una sola pantalla. Mapa con pins
// de los choferes activos hoy, sidebar derecha con la lista, y al
// seleccionar uno se abre un drawer izquierdo con el detalle (KPIs +
// timeline de stops). Toda la data viene de useLiveFleet (polling 20s).

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";

import { api } from "../data/api";
import { useDivisionMode } from "../components/ui";
import type { RouteStopRow } from "../data/schemaTypes";
import { stopDisplayName, stopHash } from "../data/stopLabel";
import {
  driverAlert,
  driverProgress,
  useLiveFleet,
  type LiveVehicle,
} from "../features/lastmile/useLiveFleet";
import { fleetTotals } from "../features/lastmile/fleetStats";

export function TrackingPage() {
  // El modo (CONSUMO/SALUD) viene del sidebar. La misma UI sirve para
  // ambos — `useLiveFleet` cambia la fuente según el modo y devuelve el
  // mismo shape `LiveVehicle[]`. En SALUD los pins/cards muestran al
  // repartidor (DNI + nombre) en lugar de placa + chofer.
  const [division] = useDivisionMode();
  // Hoy en local (no UTC). Fijo — esta pantalla es "operación en vivo".
  const todayISO = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  const { vehicles, loading, error } = useLiveFleet(
    20_000,
    todayISO,
    undefined,
    division,
  );
  // Deep-link: ?vehicle=<id> abre Operación en Vivo ya filtrada por ese
  // vehículo. Lo usa "Ver mapa" desde Supervisión. Si el id no existe
  // en la lista todavía (carga async), seteamos selectedId igual; el
  // efecto de abajo lo limpia si nunca aparece.
  const initialVehicleId = useMemo(() => {
    if (typeof window === "undefined") return null;
    const sp = new URLSearchParams(window.location.search);
    return sp.get("vehicle");
  }, []);
  const [selectedId, setSelectedId] = useState<string | null>(initialVehicleId);
  // Stop seleccionado para drill-down dentro del drawer. Solo aplica
  // cuando hay un chofer seleccionado; al cambiar de chofer se limpia.
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);

  // `vehicles` ya viene filtrado: solo los que tienen ruta del día.
  // El mapa filtra adicionalmente por GPS — los sin lat/lon no se pintan.
  // Ordenamos para que arriba salgan los conectados (GPS reciente),
  // después los que pingaron alguna vez (más reciente primero), y al
  // final los que nunca pingaron. Desempate estable por placa para
  // que el orden no salte entre polls.
  // Orden de la sidebar: con GPS desactivado, "conectados" pasa a ser
  // "operando" = vehículos con al menos un pedido atendido del día.
  // Más reciente arriba; los que aún no entregaron al final.
  const active = useMemo(() => {
    return [...vehicles].sort((a, b) => {
      const ta = a.lastStopAt?.getTime() ?? 0;
      const tb = b.lastStopAt?.getTime() ?? 0;
      if (ta !== tb) return tb - ta;
      return (a.plate ?? "").localeCompare(b.plate ?? "");
    });
  }, [vehicles]);
  // Vehículos a renderizar en el mapa: con GPS desactivado, usamos la
  // ubicación del último pedido atendido del día. Si ninguno se atendió
  // todavía, el vehículo no aparece en el mapa (sin posición conocida).
  const withGps = useMemo(
    () => active.filter((d) => d.lastStopLat != null && d.lastStopLon != null),
    [active],
  );
  const totals = useMemo(() => fleetTotals(active), [active]);

  // Stops por vehículo — se cargan una vez al saber qué rutas hay activas.
  // Cada entrada es la lista de stops de esa ruta. Map: vehicleId → stops.
  // Permite pintar TODOS los puntos en el mapa coloreados por zona, y
  // al seleccionar un vehículo filtrar para mostrar SOLO los suyos.
  const [stopsByDriver, setStopsByDriver] = useState<Map<string, RouteStopRow[]>>(
    new Map(),
  );

  useEffect(() => {
    if (active.length === 0) return;
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        active.map(async (d) => {
          try {
            const route = await api.getRoute(d.routeId);
            return [d.id, (route.stops ?? []) as RouteStopRow[]] as const;
          } catch {
            return [d.id, [] as RouteStopRow[]] as const;
          }
        }),
      );
      if (cancelled) return;
      setStopsByDriver(new Map(entries));
    })();
    return () => {
      cancelled = true;
    };
    // Re-cargamos cuando cambia el set de route IDs activos. No depender
    // de `active` entero (eso re-fetcharía cada 20s por el polling).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.map((d) => d.routeId).join(",")]);

  // Si el seleccionado deja de estar en la lista (cambió la fecha,
  // se eliminó la ruta, etc.), limpiamos la selección automáticamente.
  // OJO: NO limpiamos mientras el fetch inicial está en curso o llegó
  // vacío — un deep-link `?vehicle=<id>` setea selectedId antes de que
  // `active` se popule, y si limpiamos acá se pierde la intención.
  useEffect(() => {
    if (loading || active.length === 0) return;
    if (selectedId && !active.some((d) => d.id === selectedId)) {
      setSelectedId(null);
    }
  }, [selectedId, active, loading]);

  const selectedDriver = useMemo(
    () => active.find((d) => d.id === selectedId) ?? null,
    [active, selectedId],
  );

  // Stops a renderizar en el mapa. Vista por defecto = solo vehículos
  // (sin clientes) para que el supervisor vea la flota limpia. Cuando
  // se selecciona un vehículo, aparecen sus paradas; al limpiar la
  // selección desaparecen otra vez. Mostrar TODOS los clientes
  // simultáneamente saturaba el mapa con cientos de pines.
  const mapStops = useMemo<Array<{ stop: RouteStopRow; vehicleId: string }>>(() => {
    if (!selectedDriver) return [];
    return (stopsByDriver.get(selectedDriver.id) ?? []).map((s) => ({
      stop: s,
      vehicleId: selectedDriver.id,
    }));
  }, [stopsByDriver, selectedDriver]);

  // Stops a mostrar en el drawer (lista de paradas) — siempre los del
  // vehículo seleccionado.
  const driverStops = useMemo<RouteStopRow[]>(() => {
    if (!selectedDriver) return [];
    return (stopsByDriver.get(selectedDriver.id) ?? [])
      .slice()
      .sort((a, b) => {
        // Stops sin SECUENCIA del Excel (sequence === null) van al
        // final; dentro de cada grupo desempatamos por client_code.
        if (a.sequence !== null && b.sequence !== null) {
          return a.sequence - b.sequence;
        }
        if (a.sequence !== null) return -1;
        if (b.sequence !== null) return 1;
        return (a.client_code ?? "").localeCompare(b.client_code ?? "");
      });
  }, [stopsByDriver, selectedDriver]);

  if (loading && vehicles.length === 0) {
    return <Centered title="Cargando operación en vivo…" />;
  }
  if (error && vehicles.length === 0) {
    return (
      <Centered title="No se pudo cargar el tracking">
        <p className="text-sm text-sub">{error}</p>
      </Centered>
    );
  }
  if (active.length === 0) {
    return (
      <Centered title="Sin operación activa para hoy">
        <p className="text-sm text-sub mb-3">
          Los vehículos aparecen cuando tienen rutas programadas para la fecha actual.
          Verificá que importaste el Excel o programaste las rutas del día.
        </p>
        <Link
          to="/ultima-milla/importar"
          className="inline-block bg-primary hover:bg-primary-hover text-white text-sm font-medium rounded-md px-3 py-1.5"
        >
          Ir a Importar planificación
        </Link>
      </Centered>
    );
  }

  // Centro del mapa: si hay seleccionado, su última posición POD;
  // sino el primer vehículo con POD; sino fallback a Arequipa.
  // GPS desactivado — usamos lastStopLat/Lon (ya no lat/lon, que
  // serían siempre undefined).
  const center: [number, number] = selectedDriver?.lastStopLat != null
    ? [selectedDriver.lastStopLat, selectedDriver.lastStopLon!]
    : withGps.length > 0
      ? [withGps[0].lastStopLat!, withGps[0].lastStopLon!]
      : [-16.4, -71.54]; // Arequipa fallback

  return (
    <div className="h-full flex flex-col">
      {/* Banda KPIs globales arriba */}
      <KpiBand totals={totals} loading={loading} />

      {/* Cuerpo: drawer (opcional) | mapa | sidebar derecha */}
      <div className="flex-1 flex min-h-0">
        {selectedDriver && (
          <DriverDrawer
            driver={selectedDriver}
            stops={driverStops}
            selectedStopId={selectedStopId}
            onSelectStop={setSelectedStopId}
            onClose={() => {
              setSelectedId(null);
              setSelectedStopId(null);
            }}
          />
        )}
        <div className="relative flex-1 min-h-0">
          <MapContainer
            center={center}
            zoom={12}
            maxZoom={22}
            className="h-full w-full"
            style={{ background: "#e5e7eb" }}
          >
            <TileLayer
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={22}
              maxNativeZoom={19}
            />
            <FitMap
              drivers={withGps}
              stops={mapStops.map((m) => m.stop)}
              selectedDriverId={selectedId}
              selectedStopId={selectedStopId}
            />
            {/* Stops del día. Sin selección → coloreados por chofer/
                vehículo (mismo color que la barra de la card en la
                sidebar). Con selección → hormigas por status para
                resaltar avance operativo (verde/naranja/rojo). */}
            {mapStops.map(({ stop: s, vehicleId }) => {
              if (s.lat == null || s.lon == null) return null;
              return (
                <Marker
                  key={s.id}
                  position={[s.lat, s.lon]}
                  icon={stopIcon(
                    s,
                    s.id === selectedStopId,
                    selectedDriver ? "status" : "vehicle",
                    selectedStopId != null,
                    vehicleColor(vehicleId),
                  )}
                  eventHandlers={{
                    click: () => {
                      // Click en stop del mapa — abre drill-down. Solo
                      // tiene sentido si hay un chofer seleccionado (sino
                      // el drawer no está visible).
                      if (selectedDriver) setSelectedStopId(s.id);
                    },
                  }}
                />
              );
            })}
            {/* Pins de choferes: pintados arriba de los stops (z-index
                natural por orden de render) */}
            {withGps.map((d) => (
              <Marker
                key={d.id}
                position={[d.lastStopLat!, d.lastStopLon!]}
                icon={driverIcon(d, d.id === selectedId)}
                eventHandlers={{
                  click: () => {
                    setSelectedId(d.id);
                    setSelectedStopId(null);
                  },
                }}
              >
                <Popup>
                  <DriverPopup driver={d} />
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        <aside className="w-80 bg-card border-l border-border flex flex-col">
          <div className="p-3 border-b border-border">
            <h2 className="text-[11px] uppercase tracking-wide text-sub mb-1">
              Choferes activos hoy
            </h2>
            <p className="text-sm font-mono text-ink">
              {active.length} chofer{active.length === 1 ? "" : "es"} ·{" "}
              {withGps.length} con GPS
            </p>
          </div>
          <div className="p-3 flex-1 overflow-auto space-y-2">
            {active.map((d) => (
              <DriverCard
                key={d.id}
                driver={d}
                selected={selectedId === d.id}
                onClick={() => {
                  setSelectedId(d.id);
                  setSelectedStopId(null);
                }}
              />
            ))}
          </div>
          <div className="p-3 border-t border-border text-[11px] text-sub leading-snug">
            Auto-refresh cada 20s. Tocá un chofer para ver su detalle.
          </div>
        </aside>
      </div>
    </div>
  );
}

// ----------------- KPIs band -----------------

function KpiBand({
  totals,
  loading,
}: {
  totals: ReturnType<typeof fleetTotals>;
  loading: boolean;
}) {
  return (
    <div className="bg-card border-b border-border px-4 py-2 flex items-center gap-6 text-sm">
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-ink tabular-nums">
          {totals.efVisita.toFixed(0)}%
        </span>
        <span className="text-xs text-sub">avance</span>
      </div>
      <Divider />
      <KpiCell label="Entregadas" value={totals.done} total={totals.stops} />
      <KpiCell label="Rechazadas" value={totals.failed} accent="failed" />
      <KpiCell label="Con GPS" value={totals.active} total={totals.programadas} />
      <KpiCell label="Alertas" value={totals.alerts} accent={totals.alerts > 0 ? "failed" : undefined} />
      {loading && (
        <span className="text-[11px] text-sub ml-auto italic">actualizando…</span>
      )}
    </div>
  );
}

function Divider() {
  return <span className="h-6 w-px bg-border" />;
}

function KpiCell({
  label,
  value,
  total,
  accent,
}: {
  label: string;
  value: number;
  total?: number;
  accent?: "failed";
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span
        className={`text-base font-semibold tabular-nums ${
          accent === "failed" ? "text-state-failed" : "text-ink"
        }`}
      >
        {value}
        {total != null && <span className="text-xs text-sub">/{total}</span>}
      </span>
      <span className="text-xs text-sub">{label}</span>
    </div>
  );
}

// ----------------- Card de chofer (sidebar derecha) -----------------

function DriverCard({
  driver: d,
  selected,
  onClick,
}: {
  driver: LiveVehicle;
  selected: boolean;
  onClick: () => void;
}) {
  const pct = driverProgress(d);
  const alert = driverAlert(d);
  // "Operando" = tiene al menos un pedido atendido del día. Reemplaza
  // al chip GPS (que estaba basado en pings del device, ahora desact).
  const operating = d.lastStopAt != null;
  const color = vehicleColor(d.id);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded border transition-colors overflow-hidden flex items-stretch ${
        selected
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : alert !== "none"
            ? "border-state-failed/40 hover:bg-page"
            : "border-border hover:bg-page"
      }`}
    >
      {/* Rectángulo coloreado a la izquierda — matchea el color de los
          pins del vehículo en el mapa. Identificación visual rápida. */}
      <span
        className="w-1.5 shrink-0"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0 p-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-ink truncate font-medium font-mono">
            {d.plate}
          </span>
          <span
            className={`w-2 h-2 rounded-full shrink-0 ${
              operating ? "bg-state-delivered" : "bg-sub"
            }`}
            title={
              operating
                ? d.lastStopAt
                  ? `Operando — último POD ${d.lastStopAt.toLocaleTimeString("es-PE", { hour12: false })}`
                  : "Operando"
                : "Sin entregas todavía"
            }
          />
        </div>
        <div className="mt-1.5 h-1 rounded-full bg-page overflow-hidden">
          <div
            className={`h-full ${pct >= 1 ? "bg-state-delivered" : "bg-primary"}`}
            style={{ width: `${Math.min(pct * 100, 100)}%` }}
          />
        </div>
        <div className="text-[10px] text-sub mt-0.5 truncate">
          {d.driverFullName ?? <span className="italic">sin chofer</span>}
        </div>
        <div className="text-[10px] text-sub mt-1 flex justify-between">
          <span>
            {d.stopsDone}/{d.stopsTotal} entregadas
          </span>
          {d.stopsFailed > 0 && (
            <span className="text-state-failed">{d.stopsFailed} rechazos</span>
          )}
        </div>
      </div>
    </button>
  );
}

// ----------------- Drawer detalle del chofer -----------------

function DriverDrawer({
  driver: d,
  stops,
  selectedStopId,
  onSelectStop,
  onClose,
}: {
  driver: LiveVehicle;
  stops: RouteStopRow[];
  selectedStopId: string | null;
  onSelectStop: (id: string | null) => void;
  onClose: () => void;
}) {
  const selectedStop = useMemo(
    () => stops.find((s) => s.id === selectedStopId) ?? null,
    [stops, selectedStopId],
  );

  // Modo drill-down: si hay un stop seleccionado, mostramos su detalle
  // (boletas, estado) en lugar de los KPIs + lista. El header tiene un
  // botón "← Volver" para retornar al detalle del chofer.
  if (selectedStop) {
    return (
      <StopDetailDrawer
        driver={d}
        stop={selectedStop}
        onBack={() => onSelectStop(null)}
        onClose={onClose}
      />
    );
  }

  const pct = driverProgress(d);
  const alert = driverAlert(d);
  const totalAmount = stops.reduce<number>(
    (sum, s) => sum + (s.total_amount ?? 0),
    0,
  );

  return (
    <aside className="w-[380px] bg-card border-r border-border flex flex-col overflow-hidden">
      <header className="px-4 py-3 border-b border-border flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <AntIcon status={alert !== "none" ? "failed" : "in_progress"} size={32} />
            <div className="min-w-0">
              <div className="font-semibold text-ink font-mono text-sm">{d.plate}</div>
              <div className="text-xs text-sub truncate">
                {d.driverFullName ?? <span className="italic">sin chofer asignado</span>}
              </div>
            </div>
          </div>
        </div>
        <button onClick={onClose} className="text-sub hover:text-ink p-1" title="Cerrar">
          ✕
        </button>
      </header>

      {/* KPIs grandes */}
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-3xl font-bold text-ink tabular-nums">
            {Math.round(pct * 100)}%
          </span>
          <span className="text-xs text-sub">avance</span>
        </div>
        <div className="h-2 rounded-full bg-page overflow-hidden mb-3">
          <div
            className={`h-full transition-all ${pct >= 1 ? "bg-state-delivered" : "bg-primary"}`}
            style={{ width: `${Math.min(pct * 100, 100)}%` }}
          />
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <Stat label="Entregadas" value={`${d.stopsDone}`} color="text-state-delivered" />
          <Stat label="Pendientes" value={`${d.stopsTotal - d.stopsDone - d.stopsFailed}`} />
          <Stat
            label="Rechazos"
            value={`${d.stopsFailed}`}
            color={d.stopsFailed > 0 ? "text-state-failed" : undefined}
          />
        </div>
        {totalAmount > 0 && (
          <div className="mt-3 pt-3 border-t border-border text-center">
            <div className="text-[11px] text-sub">Total ruta</div>
            <div className="text-lg font-mono font-semibold text-ink">
              S/ {totalAmount.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        )}
      </div>

      {/* Banner de alerta si corresponde */}
      {alert !== "none" && (
        <div className="px-4 py-2 bg-state-failed/10 border-b border-state-failed/30 text-xs text-state-failed">
          {alert === "gpsLost" && "⚠ GPS perdido — sin pings hace >30 min"}
          {alert === "behindSchedule" && "⚠ Atrasado — <50% del avance esperado"}
        </div>
      )}

      {/* Lista de stops clickeable — drill-down al detalle */}
      <div className="flex-1 overflow-auto">
        <div className="px-4 py-2 text-[11px] uppercase tracking-wide text-sub font-medium border-b border-border bg-page/30">
          Paradas ({stops.length}) · tap para detalle
        </div>
        {stops.length === 0 ? (
          <div className="p-6 text-center text-sub text-sm">Cargando paradas…</div>
        ) : (
          <ol className="divide-y divide-border">
            {stops.map((s) => (
              <StopRow key={s.id} stop={s} onClick={() => onSelectStop(s.id)} />
            ))}
          </ol>
        )}
      </div>
    </aside>
  );
}

// ----------------- Detalle del stop (drill-down) -----------------

function StopDetailDrawer({
  driver: d,
  stop,
  onBack,
  onClose,
}: {
  driver: LiveVehicle;
  stop: RouteStopRow;
  onBack: () => void;
  onClose: () => void;
}) {
  // Lazy load de los sales_documents del stop. Si el endpoint falla
  // (cuenta sin import), no rompe — solo no se muestra la card.
  type SalesDoc = {
    id: string;
    doc_type: string;
    series: string;
    number: string;
    motivo?: string;
    importe: number;
    estado?: string;
    motivo_devolucion?: string;
    delivered_at?: string;
    returned_at?: string;
  };
  const [docs, setDocs] = useState<SalesDoc[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingDocs(true);
    (async () => {
      try {
        // El endpoint devuelve { data: [...], count: N }.
        const res = await fetch(`/api/orders/${stop.order_id}/sales-documents`);
        const json = (await res.json()) as { data?: SalesDoc[] };
        if (!cancelled) setDocs(json.data ?? []);
      } catch {
        if (!cancelled) setDocs([]);
      } finally {
        if (!cancelled) setLoadingDocs(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stop.order_id]);

  const totalDocs = docs.reduce((s, d) => s + d.importe, 0);

  return (
    <aside className="w-[380px] bg-card border-r border-border flex flex-col overflow-hidden">
      <header className="px-4 py-3 border-b border-border flex items-center gap-2">
        <button
          onClick={onBack}
          className="text-sub hover:text-ink p-1 -ml-1"
          title="Volver al vehículo"
        >
          ←
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-sub uppercase tracking-wide">
            Parada · vehículo {d.plate}
          </div>
          <div className="font-semibold text-ink text-sm truncate">
            {stopDisplayName(stop)}
          </div>
        </div>
        <button onClick={onClose} className="text-sub hover:text-ink p-1" title="Cerrar">
          ✕
        </button>
      </header>

      <div className="flex-1 overflow-auto">
        {/* Card de cliente */}
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-start gap-3">
            <AntIcon status={stop.status} size={36} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-ink text-sm">
                {stop.client_name || "Sin nombre"}
              </div>
              {stop.client_code && (
                <div className="text-xs text-sub font-mono">{stop.client_code}</div>
              )}
              <div className="mt-1 flex items-center gap-2 text-[11px]">
                <StatusBadge status={stop.status} />
                {stop.seller_zone && (
                  <span
                    className="font-mono px-1.5 py-0.5 rounded border text-[10px]"
                    style={{
                      color: zoneColor(stop.seller_zone),
                      borderColor: zoneColor(stop.seller_zone),
                    }}
                  >
                    {stop.seller_zone}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Card de orden — monto + secuencia */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[11px] text-sub uppercase">Orden {stopHash(stop)}</div>
            {stop.planned_arrival && (
              <div className="text-xs text-sub">
                ETA{" "}
                {new Date(stop.planned_arrival).toLocaleTimeString("es-PE", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-lg font-mono font-semibold text-ink">
              S/{" "}
              {(stop.total_amount ?? 0).toLocaleString("es-PE", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="text-[10px] text-sub">{docs.length} documentos</div>
          </div>
        </div>

        {/* Card de boletas / facturas */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wide text-sub font-medium">
              📃 Documentos ({docs.length})
            </span>
            {docs.length > 0 && (
              <span className="text-xs font-mono text-ink">
                S/ {totalDocs.toFixed(2)}
              </span>
            )}
          </div>
          {loadingDocs ? (
            <div className="text-center text-sub text-xs py-4">Cargando…</div>
          ) : docs.length === 0 ? (
            <div className="text-center text-sub text-xs py-4">
              Sin documentos vinculados
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map((doc) => {
                const isReturn = (doc.motivo ?? "").toUpperCase() === "DEVOLUCION";
                const closed =
                  (doc.delivered_at && doc.delivered_at.length > 0) ||
                  (doc.returned_at && doc.returned_at.length > 0);
                return (
                  <div
                    key={doc.id}
                    className="flex items-start justify-between py-1.5 border-b border-border last:border-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-sub font-semibold uppercase">
                          {doc.doc_type}
                        </span>
                        <span className="text-xs font-mono text-ink">
                          {doc.series}-{doc.number}
                        </span>
                        {isReturn && (
                          <span className="text-[9px] px-1 py-0.5 rounded bg-state-delayed/10 text-state-delayed border border-state-delayed/40">
                            DEV
                          </span>
                        )}
                      </div>
                      {doc.estado && (
                        <div
                          className="text-[10px] mt-0.5"
                          style={{ color: stopColor(estadoToStatus(doc.estado)) }}
                        >
                          {doc.estado}
                          {doc.motivo_devolucion && ` · ${doc.motivo_devolucion}`}
                        </div>
                      )}
                    </div>
                    <div
                      className={`text-sm font-mono font-semibold ${
                        closed && (doc.estado ?? "").toUpperCase().startsWith("DEV")
                          ? "line-through text-sub"
                          : "text-ink"
                      }`}
                    >
                      S/ {doc.importe.toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color = stopColor(status);
  return (
    <span
      className="px-1.5 py-0.5 rounded text-[10px] font-medium border"
      style={{ color, borderColor: color }}
    >
      {statusLabel(status)}
    </span>
  );
}

// Mapea estados del Excel a status interno para colorear consistente.
function estadoToStatus(estado: string): string {
  const e = estado.toUpperCase();
  if (e.includes("DEV") && e.includes("PARCIAL")) return "partial";
  if (e.includes("DEV")) return "failed";
  if (e.includes("DESPACHADO") || e.includes("ENTREGADO")) return "delivered";
  return "pending";
}

function Stat({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div>
      <div className={`text-lg font-semibold tabular-nums ${color ?? "text-ink"}`}>
        {value}
      </div>
      <div className="text-[10px] text-sub uppercase tracking-wide">{label}</div>
    </div>
  );
}

function StopRow({
  stop,
  onClick,
}: {
  stop: RouteStopRow;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full px-4 py-2.5 flex items-start gap-3 hover:bg-page text-left"
      >
        <AntIcon status={stop.status} size={24} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-ink truncate font-medium">
              {stopDisplayName(stop)}
            </span>
            {stop.client_code && (
              <span className="text-[10px] text-sub font-mono shrink-0">
                {stop.client_code}
              </span>
            )}
          </div>
          <div className="text-[11px] text-sub flex items-center gap-2 mt-0.5">
            <span>{statusLabel(stop.status)}</span>
            {stop.seller_zone && (
              <>
                <span>·</span>
                <span
                  className="font-mono px-1 rounded"
                  style={{ color: zoneColor(stop.seller_zone) }}
                >
                  {stop.seller_zone}
                </span>
              </>
            )}
            {stop.total_amount != null && stop.total_amount > 0 && (
              <>
                <span>·</span>
                <span className="font-mono">S/ {stop.total_amount.toFixed(2)}</span>
              </>
            )}
          </div>
        </div>
        <span className="text-sub text-sm self-center">›</span>
      </button>
    </li>
  );
}

function statusLabel(s: string): string {
  return (
    {
      delivered: "Entregada",
      partial: "Entrega parcial",
      failed: "Rechazada",
      skipped: "Omitida",
      in_progress: "En curso",
      pending: "Pendiente",
    } as Record<string, string>
  )[s] ?? s;
}

// ----------------- Hormiga icon (CSS-only, sin SVG) -----------------

function AntIcon({ status, size }: { status: string; size: number }) {
  const color = stopColor(status);
  return (
    <div
      className="flex items-center justify-center shrink-0 rounded-full bg-card border-2"
      style={{ width: size, height: size, borderColor: color }}
    >
      <svg
        viewBox="0 0 64 64"
        width={size * 0.66}
        height={size * 0.66}
        fill={color}
      >
        {/* Antenas */}
        <path d="M40 18 Q44 10 47 6" stroke={color} strokeWidth={2.5} fill="none" strokeLinecap="round" />
        <path d="M44 18 Q49 12 53 9" stroke={color} strokeWidth={2.5} fill="none" strokeLinecap="round" />
        {/* Cuerpo */}
        <ellipse cx="18" cy="36" rx="14" ry="10" />
        <circle cx="34" cy="34" r="8" />
        <circle cx="46" cy="28" r="8" />
        {/* Caja */}
        <rect x="22" y="14" width="22" height="18" rx="2" fill="white" stroke={color} strokeWidth={2} transform="rotate(-8 33 23)" />
        {/* Patas */}
        <g stroke={color} strokeWidth={2.5} strokeLinecap="round" fill="none">
          <path d="M14 44 L8 56" />
          <path d="M22 46 L20 58" />
          <path d="M30 44 L34 58" />
          <path d="M38 42 L46 56" />
          <path d="M48 36 L58 44" />
        </g>
      </svg>
    </div>
  );
}

function stopColor(s: string): string {
  return (
    {
      delivered: "#2E7D32",
      partial: "#FB8C00",
      failed: "#D32F2F",
      skipped: "#D32F2F",
      in_progress: "#1976D2",
      pending: "#1976D2",
    } as Record<string, string>
  )[s] ?? "#90A4AE";
}

// ----------------- Mapa: marker + popup + fit -----------------

function FitMap({
  drivers,
  stops,
  selectedDriverId,
  selectedStopId,
}: {
  drivers: LiveVehicle[];
  stops: RouteStopRow[];
  selectedDriverId: string | null;
  selectedStopId: string | null;
}) {
  const map = useMap();
  // Hash estable de los stops actuales — dep del useEffect para
  // re-encuadrar cuando los stops llegan async (caso típico al
  // seleccionar chofer y los route stops detail aún cargando).
  const stopsKey = useMemo(
    () => stops.map((s) => s.id).join(","),
    [stops],
  );
  // El encuadre se hace SIEMPRE sobre los stops (geográficamente
  // estables, vienen del Excel). NUNCA sobre `drivers[].lat/lon` —
  // un GPS desactualizado de otra ciudad / del simulador rompe el
  // bbox y manda el mapa a "continente". El pin del chofer queda
  // donde esté pero no afecta el encuadre.
  //
  // Modos:
  //   1. Stop seleccionado → flyTo al stop (más cerca)
  //   2. Chofer seleccionado → bounds de SUS stops
  //   3. Sin selección → bounds de todos los stops
  useEffect(() => {
    void drivers; // referenciar para que TS no marque unused
    if (selectedStopId) {
      const s = stops.find((x) => x.id === selectedStopId);
      if (s?.lat != null && s?.lon != null) {
        map.flyTo([s.lat, s.lon], Math.max(map.getZoom(), 16), { duration: 0.5 });
      }
      return;
    }
    // Cuando hay chofer seleccionado, `stops` ya viene filtrado a sus
    // stops desde TrackingPage (mapStops). Sin selección, viene con
    // todos los stops del día. Misma lógica de encuadre para ambos.
    //
    // Filtrado de coords inválidas:
    //   1. null/undefined → fuera
    //   2. (0, 0) o casi cero → fuera (centro oceánico, dato sin coord)
    //   3. Outliers groseros → fuera (cliente con coord cargada mal en
    //      el Excel, p.ej. orden con lat 19.5 cuando el resto está
    //      en Arequipa -16.4). Detectamos con median absolute deviation:
    //      cualquier punto a >3° (≈330 km) de la mediana se descarta.
    let pts = collectValidCoords(stops);
    pts = filterOutliersByMedian(pts, 3);
    if (pts.length === 1) {
      map.flyTo(pts[0], 15, { duration: 0.6 });
    } else if (pts.length > 1) {
      map.fitBounds(L.latLngBounds(pts), {
        padding: [60, 60],
        maxZoom: selectedDriverId ? 15 : 14,
      });
    }
    // Si pts vacío: stops aún no llegaron. El próximo render con stops
    // cargados re-disparará gracias a stopsKey en las deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStopId, selectedDriverId, stopsKey]);
  return null;
}

/// Marker para un stop. Dos modos:
///   - `vehicle`: punto chico coloreado por chofer/vehículo (vista global,
///     sin selección). El color matchea la barra izquierda de la card
///     en la sidebar — así el operador identifica visualmente qué pin
///     pertenece a cada chofer.
///   - `status`: hormiga grande coloreada por status (pending=azul,
///     partial=naranja, failed=rojo, delivered=verde). Se usa cuando hay
///     chofer seleccionado para enfatizar el progreso operativo.
///
/// Cuando hay un stop seleccionado, los demás se atenúan (opacity 0.3)
/// y el seleccionado recibe halo pulsante + label flotante con el nombre.
function stopIcon(
  stop: RouteStopRow,
  selected: boolean,
  mode: "vehicle" | "status",
  hasSelection: boolean,
  /// Color asignado al vehículo dueño del stop — solo relevante para
  /// modo "vehicle". En modo "status" se ignora.
  vehicleColorHex: string,
): L.DivIcon {
  if (mode === "status") {
    return antStopIcon(stop, selected, hasSelection);
  }
  const color = vehicleColorHex;
  const size = selected ? 18 : 12;
  const ring = selected
    ? "0 0 0 3px rgba(255,143,0,0.7)"
    : "0 1px 2px rgba(0,0,0,0.4)";
  // Atenuamos los pins no-seleccionados cuando hay selección — el ojo
  // va directo al brillante. La clase con transition suaviza el cambio.
  const dimClass = hasSelection && !selected ? "micelio-stop-dim" : "";
  const html = `
    <div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${color};border:2px solid white;
      box-shadow:${ring};
    "></div>
  `;
  return L.divIcon({
    html,
    className: dimClass,
    iconSize: [size + 4, size + 4],
    iconAnchor: [(size + 4) / 2, (size + 4) / 2],
  });
}

/// Hormiga inline para los stops cuando hay chofer seleccionado. Misma
/// silueta que el componente <AntIcon> de React, pero como HTML/SVG plano
/// porque Leaflet DivIcon recibe `html: string`.
///
/// El stop seleccionado lleva (a) halo pulsante naranja, (b) label sticky
/// con el nombre del cliente flotando arriba — patrón Uber/Google Maps
/// para que sea imposible perderlo entre los demás pins.
function antStopIcon(
  stop: RouteStopRow,
  selected: boolean,
  hasSelection: boolean,
): L.DivIcon {
  const color = stopColor(stop.status);
  const size = selected ? 40 : 26;
  const ring = selected
    ? "0 2px 8px rgba(0,0,0,0.35)"
    : "0 1px 3px rgba(0,0,0,0.35)";
  const inner = Math.round(size * 0.66);
  const dimClass = hasSelection && !selected ? "micelio-stop-dim" : "";
  const selClass = selected ? "micelio-stop-selected" : "";
  const label = selected
    ? `<div class="stop-label">${escapeHtml(
        stopDisplayName(stop),
      )}</div>`
    : "";
  const halo = selected
    ? `<div class="halo"></div><div class="halo delay"></div>`
    : "";
  const html = `
    ${halo}
    ${label}
    <div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:white;border:${selected ? 3 : 2}px solid ${color};
      box-shadow:${ring};
      display:flex;align-items:center;justify-content:center;
    ">
      <svg viewBox="0 0 64 64" width="${inner}" height="${inner}" fill="${color}">
        <path d="M40 18 Q44 10 47 6" stroke="${color}" stroke-width="2.5" fill="none" stroke-linecap="round"/>
        <path d="M44 18 Q49 12 53 9" stroke="${color}" stroke-width="2.5" fill="none" stroke-linecap="round"/>
        <ellipse cx="18" cy="36" rx="14" ry="10"/>
        <circle cx="34" cy="34" r="8"/>
        <circle cx="46" cy="28" r="8"/>
        <rect x="22" y="14" width="22" height="18" rx="2" fill="white" stroke="${color}" stroke-width="2" transform="rotate(-8 33 23)"/>
        <g stroke="${color}" stroke-width="2.5" stroke-linecap="round" fill="none">
          <path d="M14 44 L8 56"/>
          <path d="M22 46 L20 58"/>
          <path d="M30 44 L34 58"/>
          <path d="M38 42 L46 56"/>
          <path d="M48 36 L58 44"/>
        </g>
      </svg>
    </div>
  `;
  return L.divIcon({
    html,
    className: `${dimClass} ${selClass}`.trim(),
    iconSize: [size + 4, size + 4],
    iconAnchor: [(size + 4) / 2, (size + 4) / 2],
  });
}

// Sanitiza texto antes de inyectarlo en `html` del divIcon — los nombres
// del Excel pueden traer "&", "<", etc. y romper el SVG circundante.
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/// Color HSL estable derivado del código de zona (ej. CV101, CH059).
/// Misma fórmula que mobile (route_map_view.dart) para coherencia visual
/// — un vendedor tiene el mismo color en mobile y web.
function zoneColor(zone: string): string {
  if (!zone) return "#90A4AE";
  let seed = 0;
  for (let i = 0; i < zone.length; i++) seed = seed * 31 + zone.charCodeAt(i);
  const h = Math.abs(seed * 137.508) % 360;
  return `hsl(${h.toFixed(0)}, 65%, 45%)`;
}

/// Color estable por vehículo/chofer. Mismo algoritmo que zoneColor pero
/// sobre el vehicle_id — así cada vehículo tiene un color único y
/// constante (no cambia entre refreshes). Usado para:
///   - pintar los pins del mapa según a qué chofer pertenecen
///   - barra izquierda de cada card en la sidebar (matching visual)
/// Saturación/luminosidad un poco más oscuros que zoneColor para
/// distinguir el caso "vehicle" del legacy "zone".
function vehicleColor(vehicleId: string): string {
  if (!vehicleId) return "#90A4AE";
  let seed = 0;
  for (let i = 0; i < vehicleId.length; i++) seed = seed * 31 + vehicleId.charCodeAt(i);
  const h = Math.abs(seed * 137.508) % 360;
  return `hsl(${h.toFixed(0)}, 70%, 42%)`;
}

function driverIcon(d: LiveVehicle, selected: boolean): L.DivIcon {
  // El pin del vehículo va en la última ubicación del POD del día con
  // el color identitario del vehículo (matchea con la barra de la
  // card en la sidebar y los pins de stops del mismo vehículo). GPS
  // desactivado — el color ya no depende de frescura GPS.
  const color = vehicleColor(d.id);
  const ring = selected ? "0 0 0 4px rgba(255,143,0,0.6)" : "0 2px 4px rgba(0,0,0,0.35)";
  // SVG de camión (cabina + caja + ruedas) — silueta blanca sobre el
  // círculo coloreado. La caja queda a la izquierda y la cabina a la
  // derecha — orientación clásica de delivery truck visto de costado.
  const truck = `
    <svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="7" width="12" height="9" rx="0.8"/>
      <path d="M14 9 L20.2 9 Q21 9 21.4 9.6 L22.6 11.4 Q23 12 23 12.6 L23 16 Q23 16.6 22.4 16.6 L14 16.6 Z"/>
      <rect x="15.5" y="10.2" width="3.8" height="3" fill="${color}"/>
      <circle cx="7" cy="17.5" r="2.2" fill="#222"/>
      <circle cx="7" cy="17.5" r="0.9" fill="white"/>
      <circle cx="18.5" cy="17.5" r="2.2" fill="#222"/>
      <circle cx="18.5" cy="17.5" r="0.9" fill="white"/>
    </svg>
  `;
  const html = `
    <div style="
      width:36px;height:36px;border-radius:50%;
      background:${color};border:2px solid white;
      box-shadow:${ring};
      display:flex;align-items:center;justify-content:center;
    ">
      <div style="width:24px;height:24px;">${truck}</div>
    </div>
  `;
  return L.divIcon({
    html,
    className: "",
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

function DriverPopup({ driver: d }: { driver: LiveVehicle }) {
  const pct = Math.round(driverProgress(d) * 100);
  return (
    <div style={{ minWidth: 180 }}>
      <div style={{ fontWeight: 600, marginBottom: 2, fontFamily: "monospace" }}>
        {d.plate}
      </div>
      <div style={{ fontSize: 11, color: "#5C6B7A", marginBottom: 6 }}>
        {d.driverFullName ?? "Sin chofer asignado"}
      </div>
      <div style={{ fontSize: 12, color: "#5C6B7A" }}>
        {pct}% · {d.stopsDone}/{d.stopsTotal} entregadas
        {d.stopsFailed > 0 && ` · ${d.stopsFailed} rechazos`}
      </div>
    </div>
  );
}

function Centered({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="max-w-md bg-card border border-border rounded-lg p-6 text-center">
        <h2 className="text-ink text-lg font-semibold">{title}</h2>
        {children}
      </div>
    </div>
  );
}


// ---------- Helpers de fit-bounds ----------

/// Extrae coords válidas de un array de stops. Filtra:
///   - lat/lon null o undefined
///   - lat=0 && lon=0 (típico "sin coord" del importador)
///   - lat/lon fuera de rango terrestre (-90..90, -180..180)
function collectValidCoords(stops: RouteStopRow[]): [number, number][] {
  const out: [number, number][] = [];
  for (const s of stops) {
    if (s.lat == null || s.lon == null) continue;
    if (s.lat === 0 && s.lon === 0) continue;
    if (s.lat < -90 || s.lat > 90 || s.lon < -180 || s.lon > 180) continue;
    out.push([s.lat, s.lon]);
  }
  return out;
}

/// Descarta outliers por desviación de la mediana. Pensado para
/// proteger el bbox cuando 1-2 stops tienen coords erróneas (cargadas
/// mal en el Excel, cliente con dirección rara) — sino expanden la
/// vista a nivel país/continente.
///
/// `maxDeltaDeg` = distancia máxima (en grados) que un punto puede
/// estar de la mediana para conservarse. 3° ≈ 330 km — suficiente
/// para una operación urbana o de varias ciudades cercanas.
function filterOutliersByMedian(
  pts: [number, number][],
  maxDeltaDeg: number,
): [number, number][] {
  if (pts.length < 3) return pts; // muy pocos para descartar
  const lats = pts.map(([la]) => la).sort((a, b) => a - b);
  const lons = pts.map(([, lo]) => lo).sort((a, b) => a - b);
  const medLat = lats[Math.floor(lats.length / 2)];
  const medLon = lons[Math.floor(lons.length / 2)];
  return pts.filter(
    ([la, lo]) =>
      Math.abs(la - medLat) <= maxDeltaDeg &&
      Math.abs(lo - medLon) <= maxDeltaDeg,
  );
}
